import { initializeApp } from "firebase/app";
import { isSupported } from "firebase/analytics";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import {
    connectAuthEmulator,
    getAuth,
    isSignInWithEmailLink as authIsSignInWithEmailLink,
    onAuthStateChanged,
    signInWithCustomToken,
    signOut,
    User,
    UserCredential,
} from "firebase/auth";
import config from "../../config";
import { AuthActions, OrderActions, store } from "../../redux";
import { Configuration, OperationResult, OperationState, Order } from "./types";
import { v4 as uuid } from "uuid";
import napAxios from "../../axios";
import { ConfigurationActions } from "../../redux/slices/configurationSlice";
import { defaultClient } from "../ConfigurationClient";
import { initializeAnalytics, setAnalyticsCollectionEnabled } from "firebase/analytics";
import {
    collection,
    connectFirestoreEmulator,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    onSnapshot,
} from "firebase/firestore";
import {
    deleteObject,
    getDownloadURL,
    getStorage,
    ref,
    uploadBytesResumable,
    UploadTask,
} from "@firebase/storage";
import versions from "../../../public/versions.json";
import { removeTimestamps } from "../../utils/utils";

export type AuthenticationListener = (user: User | null) => void;
export type ProgressListener = (x: number) => void;
export type ErrorListener = (e: Error) => void;
export type CompletionListener = () => void;

const firebaseApp = initializeApp(config.firebaseConfig);
const firebaseAnalytics = (async () =>
    (await isSupported()) &&
    initializeAnalytics(firebaseApp, {
        config: {
            product_tag: "NAP",
            cookie_flags: "secure;samesite=none;",
        },
    }))();

const firebaseAuth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);

if (process.env.REACT_APP_FIREBASE_EMULATOR_HOST) {
    connectAuthEmulator(
        firebaseAuth,
        `http://${process.env.REACT_APP_FIREBASE_EMULATOR_HOST}:9099`,
    );
    connectFirestoreEmulator(firestore, process.env.REACT_APP_FIREBASE_EMULATOR_HOST, 8181);
}

type UserCentricsServiceBaseInfo = {
    name: string;
    consent: { status: boolean };
};
const enableOrDisableAnalytics = async () => {
    const serviceBaseInfos: UserCentricsServiceBaseInfo[] | undefined = (
        window as any
    ).UC_UI?.getServicesBaseInfo();
    const googleAnalyticsService = serviceBaseInfos?.find((x) => x.name === "Google Analytics");
    const analyticsConsent = !!googleAnalyticsService?.consent?.status;

    console.info("analytics", analyticsConsent ? "enabled" : "disabled");

    const analytics = await firebaseAnalytics;
    analytics && setAnalyticsCollectionEnabled(analytics, analyticsConsent);
};

window.addEventListener("UC_UI_INITIALIZED", enableOrDisableAnalytics);
window.addEventListener("UC_UI_CMP_EVENT", enableOrDisableAnalytics);

export const isSignInWithEmailLink = (url: string): boolean =>
    authIsSignInWithEmailLink(firebaseAuth, url);

export interface AuthCallbacks {
    authenticated: (user: User) => void;
    unauthenticated: () => void;
}

export interface ConfigurationCallbacks {
    overwrite: (configurations: Configuration[]) => void;
}

export interface OrderCallbacks {
    setOrders: (orders: Order[]) => void;
}

export interface FirebaseCallbacks {
    authCallbacks: AuthCallbacks;
    configurationCallbacks: ConfigurationCallbacks;
    orderCallbacks: OrderCallbacks;
}

const defaultCallbacks: FirebaseCallbacks = {
    authCallbacks: {
        authenticated: (user) =>
            console.warn("authCallback not set. Called authenticated with", user),
        unauthenticated: () => console.warn("authCallback not set. Called unauthenticated"),
    },
    configurationCallbacks: {
        overwrite: (configuration) =>
            console.warn("configurationCallback not set. Called overwrite with", configuration),
    },
    orderCallbacks: {
        setOrders: (orders) => console.warn("orderCallback not set. Called overwrite with", orders),
    },
};

export class FirebaseClient {
    unsubscribeOrders: (() => void) | null = null;
    unsubscribeConfigurations: (() => void) | null = null;

    callbacks: FirebaseCallbacks;

    constructor(callbacks?: Partial<FirebaseCallbacks>) {
        this.callbacks = { ...defaultCallbacks, ...callbacks };

        const authListener = (user: User | null): void => {
            if (user) {
                this.callbacks.authCallbacks.authenticated(user);
            } else {
                this.callbacks.authCallbacks.unauthenticated();
            }
        };

        onAuthStateChanged(firebaseAuth, authListener);
    }

    public get currentUser(): User | null {
        return firebaseAuth.currentUser;
    }

    async signOut(): Promise<void> {
        return signOut(firebaseAuth);
    }

    async signInWithCustomToken(token: string): Promise<UserCredential | void> {
        console.log("signing in with", token);
        return await signInWithCustomToken(firebaseAuth, token);
    }

    checkForConfigurationUpgrade(staticMajor: number, staticMinor: number): void {
        const uid = store.getState().auth.uid;
        if (!uid) {
            return;
        }
        getDocs(collection(firestore, `/users/${uid}/configurations/`)).then(async (data) => {
            data.docs.forEach((it) => {
                const [schemaMajor, schemaMinor] = (it.data().schemaVersion ?? "0.0")
                    .split(".")
                    .map(parseInt);
                if (schemaMajor < staticMajor || schemaMinor < staticMinor) {
                    console.log(
                        `upgrading ${it.id} from ${schemaMajor}.${schemaMinor} to ${staticMajor}.${staticMinor}`,
                    );
                    defaultClient.upgrade(it.id);
                }
            });
        });
    }

    async subscribeStatic(): Promise<void> {
        onSnapshot(doc(firestore, "/static/nap"), async (snaps) => {
            const [
                firebaseConfigurationSchemaMajorVersion,
                firebaseConfigurationSchemaMinorVersion,
            ] = (snaps.data()?.configurationSchemaVersion || "0.0").split(".").map(parseInt);
            if (
                versions.configurationSchemaMajorVersion < firebaseConfigurationSchemaMajorVersion
            ) {
                console.log(
                    `Update detected from major ${versions.configurationSchemaMajorVersion} to ${firebaseConfigurationSchemaMajorVersion}, reloading client`,
                );
                window.location.reload();
            } else {
                this.checkForConfigurationUpgrade(
                    firebaseConfigurationSchemaMajorVersion,
                    firebaseConfigurationSchemaMinorVersion,
                );
            }
        });
    }

    async subscribeConfigurations(uid: string): Promise<void> {
        if (this.unsubscribeConfigurations) this.unsubscribeConfigurations();
        this.unsubscribeConfigurations = onSnapshot(
            collection(firestore, `/users/${uid}/configurations/`),
            async (snaps) => {
                const configurations = snaps.docs.map((it) => {
                    const conf = it.data();
                    conf.id = it.id;
                    return conf as Configuration;
                });
                this.callbacks.configurationCallbacks.overwrite(configurations);
            },
        );
    }

    async subscribeToOrders(uid: string): Promise<void> {
        if (this.unsubscribeOrders) this.unsubscribeOrders();
        this.unsubscribeOrders = onSnapshot(
            collection(firestore, `/users/${uid}/orders`),
            (snaps) => {
                const orders = snaps.docs.map((snap) => {
                    const order = snap.data();
                    order.id = snap.id;
                    return order as Order;
                });
                this.callbacks.orderCallbacks.setOrders(orders);
            },
        );
    }

    async unsubscribe(): Promise<void> {
        if (this.unsubscribeConfigurations) this.unsubscribeConfigurations();
        if (this.unsubscribeOrders) this.unsubscribeOrders();
    }

    uploadFile(
        path: string,
        file: File,
        contentType: string,
        customMetadata: Record<symbol, string>,
        progressListener: ProgressListener,
        errorListener: ErrorListener,
        completionListener: CompletionListener,
    ): UploadTask {
        const storageRef = ref(getStorage(firebaseApp));
        const fileRef = ref(storageRef, path);

        const uploadTask = uploadBytesResumable(fileRef, file, {
            contentType,
            contentDisposition: `attachment; filename="${file.name}"`,
            customMetadata,
        });

        const unsubscribeProgress = uploadTask.on(
            "state_changed",
            function (snapshot) {
                const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (progressListener) progressListener(percent);
            },
            errorListener,
            () => {
                unsubscribeProgress();
                completionListener();
            },
        );

        return uploadTask;
    }

    async getDownloadUrl(path: string): Promise<string> {
        return getDownloadURL(ref(getStorage(firebaseApp), path));
    }

    async removeFile(path: string): Promise<void> {
        console.log("deleting " + path);
        // const path = `users/${uid}/configurations/${configurationId}/documents/${uuidFromClassification(
        //     classification,
        // )}`;
        await deleteObject(ref(getStorage(firebaseApp), path));
    }

    async waitForOperation(uid: string, id: string): Promise<OperationState> {
        if (!uid) throw new Error("cannot execute operations without valid uid");
        const result = new Promise<OperationState>((resolve, reject) => {
            const timeoutId = setTimeout(
                () => reject(`Operation ${id} timed out!`),
                config.operationTimeout,
            );
            const unsubscribe = onSnapshot(
                collection(firestore, `/users/${uid}/operations/`),
                (snap) => {
                    snap.docChanges().forEach((docSnap) => {
                        if (docSnap.doc.id === id) {
                            console.log("operation " + id + " complete");
                            clearTimeout(timeoutId);
                            unsubscribe();
                            resolve(docSnap.doc.get("state"));
                            deleteDoc(docSnap.doc.ref);
                        }
                    });
                },
            );
        });
        return result;
    }

    async doWithOperation<T>(
        this: FirebaseClient,
        uid: string,
        operation: () => Promise<T>,
    ): Promise<OperationResult<T>> {
        const operationId = uuid();
        napAxios.operationId = operationId;
        try {
            console.time("operation");
            const result = await operation();
            const status = await this.waitForOperation(uid, operationId);
            return {
                result,
                status,
            };
        } catch (e) {
            console.error(e);
            return {
                status: OperationState.UNKNOWN_ERROR,
                result: e,
            };
        } finally {
            console.timeEnd("operation");
        }
    }
}
export const defaultFirebaseClient = new FirebaseClient({
    authCallbacks: {
        authenticated: (user) => {
            store.dispatch(
                AuthActions.authenticated({
                    isAnonymous: user.isAnonymous,
                    uid: user.uid,
                    email: user.email,
                }),
            );
            store.dispatch(ConfigurationActions.subscribeStatic());
            store.dispatch(ConfigurationActions.subscribeConfigurations());
            store.dispatch(OrderActions.subscribeOrders());
        },
        unauthenticated: () => store.dispatch(AuthActions.unauthenticated()),
    },
    orderCallbacks: {
        setOrders: (orders) => store.dispatch(OrderActions.setOrders(removeTimestamps(orders))),
    },
    configurationCallbacks: {
        overwrite: (config) => {
            config = removeTimestamps(config);
            store.dispatch(ConfigurationActions.overwrite(config));
        },
    },
});
